import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
    Plus, Pencil, Trash2, GripVertical, Upload, ImageIcon,
    ToggleLeft, ToggleRight, ExternalLink, Save, X, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const BLANK = {
    slug: '', name: '', tagline: '', description: '',
    icon: '🛍️', icon_image_url: '',
    color: '#dc2626', gradient_from: '#dc2626', gradient_to: '#991b1b',
    href: '/okgo', external: false,
    super_category_slug: '', order: 99, active: true,
};

// ─── Image Upload Helper ──────────────────────────────────────────────────────

function ImageUploader({ value, onChange, label }) {
    const ref = useRef();
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChange(data.url);
            toast.success('Imagen subida');
        } catch { toast.error('Error al subir imagen'); }
        finally { setUploading(false); }
    };

    return (
        <div className="space-y-1.5">
            <Label className="text-xs">{label}</Label>
            <div className="flex items-center gap-2">
                <div className="h-14 w-14 rounded-xl border bg-secondary/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {value
                        ? <img src={value} alt="" className="w-full h-full object-contain" />
                        : <ImageIcon className="h-5 w-5 text-muted-foreground" />
                    }
                </div>
                <div className="flex-1 space-y-1">
                    <input type="file" ref={ref} accept="image/*" className="hidden" onChange={pick} />
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => ref.current?.click()} disabled={uploading}>
                        <Upload className="h-3.5 w-3.5 mr-1.5" />{uploading ? 'Subiendo…' : 'Subir imagen'}
                    </Button>
                    {value && (
                        <Input value={value} readOnly className="text-[10px] h-7 font-mono" />
                    )}
                </div>
            </div>
            {value && (
                <button type="button" onClick={() => onChange('')} className="text-[10px] text-red-500 hover:underline">
                    Quitar imagen
                </button>
            )}
        </div>
    );
}

// ─── App Editor Dialog ────────────────────────────────────────────────────────

function AppEditor({ open, value, onChange, onSave, loading }) {
    const f = value;
    if (!f) return null;
    const set = (k, v) => onChange({ ...f, [k]: v });

    return (
        <Dialog open={open} onOpenChange={() => onChange(null)}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{f.id ? 'Editar app' : 'Nueva app de plataforma'}</DialogTitle>
                    <DialogDescription>Cada app se muestra como un ícono en el selector de entorno del home.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    {/* Basic info */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Slug * <span className="text-muted-foreground">(único, no cambia)</span></Label>
                            <Input
                                value={f.slug}
                                onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                                placeholder="okfood"
                                disabled={!!f.id}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Nombre *</Label>
                            <Input value={f.name} onChange={e => set('name', e.target.value)} placeholder="OkFood" className="mt-1" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Tagline</Label>
                            <Input value={f.tagline} onChange={e => set('tagline', e.target.value)} placeholder="Comida a tu puerta" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Ícono emoji (fallback)</Label>
                            <Input value={f.icon} onChange={e => set('icon', e.target.value)} placeholder="🍔" className="mt-1 text-xl" maxLength={4} />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Descripción</Label>
                        <textarea
                            value={f.description}
                            onChange={e => set('description', e.target.value)}
                            rows={2}
                            placeholder="Descripción breve de la app…"
                            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                    </div>

                    {/* Icon image */}
                    <ImageUploader
                        value={f.icon_image_url}
                        onChange={v => set('icon_image_url', v)}
                        label="Imagen del ícono de app (PNG/SVG transparente, 128×128 recomendado)"
                    />

                    {/* Colors */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs">Color principal</Label>
                            <div className="flex gap-2 mt-1">
                                <input type="color" value={f.color} onChange={e => set('color', e.target.value)} className="h-9 w-10 rounded border cursor-pointer" />
                                <Input value={f.color} onChange={e => set('color', e.target.value)} className="flex-1 h-9 font-mono text-xs" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Gradiente desde</Label>
                            <div className="flex gap-2 mt-1">
                                <input type="color" value={f.gradient_from} onChange={e => set('gradient_from', e.target.value)} className="h-9 w-10 rounded border cursor-pointer" />
                                <Input value={f.gradient_from} onChange={e => set('gradient_from', e.target.value)} className="flex-1 h-9 font-mono text-xs" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Gradiente hasta</Label>
                            <div className="flex gap-2 mt-1">
                                <input type="color" value={f.gradient_to} onChange={e => set('gradient_to', e.target.value)} className="h-9 w-10 rounded border cursor-pointer" />
                                <Input value={f.gradient_to} onChange={e => set('gradient_to', e.target.value)} className="flex-1 h-9 font-mono text-xs" />
                            </div>
                        </div>
                    </div>

                    {/* Preview of icon */}
                    <div className="flex items-center gap-3 rounded-xl border bg-secondary/20 p-4">
                        <div
                            className="h-16 w-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-md flex-shrink-0"
                            style={{ background: `linear-gradient(135deg, ${f.gradient_from}, ${f.gradient_to})` }}
                        >
                            {f.icon_image_url
                                ? <img src={f.icon_image_url} alt="" className="w-10 h-10 object-contain" />
                                : <span className="text-3xl">{f.icon || '🛍️'}</span>
                            }
                        </div>
                        <div>
                            <p className="font-bold text-sm">{f.name || 'Nombre de app'}</p>
                            <p className="text-xs text-muted-foreground">{f.tagline || 'Tagline de la app'}</p>
                            <p className="text-[10px] mt-0.5 font-mono" style={{ color: f.color }}>{f.slug || 'slug'}</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">URL / Ruta (href)</Label>
                            <Input value={f.href} onChange={e => set('href', e.target.value)} placeholder="/okgo?app=okfood" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Super categoría vinculada</Label>
                            <Input value={f.super_category_slug} onChange={e => set('super_category_slug', e.target.value)} placeholder="okfood" className="mt-1" />
                            <p className="text-[10px] text-muted-foreground mt-0.5">Slug de super-categoría para filtrar negocios en home.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Orden</Label>
                            <Input type="number" value={f.order} onChange={e => set('order', parseInt(e.target.value) || 0)} min={0} className="mt-1" />
                        </div>
                        <div className="space-y-2 mt-5">
                            <div className="flex items-center gap-2">
                                <Switch checked={!!f.external} onCheckedChange={v => set('external', v)} />
                                <Label className="text-xs">Enlace externo</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={!!f.active} onCheckedChange={v => set('active', v)} />
                                <Label className="text-xs">Activo en home</Label>
                            </div>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onChange(null)}>Cancelar</Button>
                    <Button onClick={onSave} disabled={loading} className="bg-red-600 hover:bg-red-700">
                        <Save className="h-4 w-4 mr-1" />{loading ? 'Guardando…' : (f.id ? 'Actualizar' : 'Crear app')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminPlatformApps() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/platform-apps');
            setApps(data || []);
        } catch { toast.error('Error cargando apps'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        if (!editing) return;
        if (!editing.slug.trim() || !editing.name.trim()) { toast.error('Slug y nombre son requeridos'); return; }
        setSaving(true);
        try {
            if (editing.id) {
                await api.put(`/admin/platform-apps/${editing.slug}`, editing);
                toast.success('App actualizada');
            } else {
                await api.post('/admin/platform-apps', editing);
                toast.success('App creada');
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
        finally { setSaving(false); }
    };

    const handleToggle = async (app) => {
        try {
            await api.put(`/admin/platform-apps/${app.slug}`, { active: !app.active });
            toast.success(app.active ? 'Desactivada' : 'Activada');
            load();
        } catch { toast.error('Error'); }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/admin/platform-apps/${deleteId}`);
            toast.success('App eliminada');
            setDeleteId(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-5 max-w-4xl">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <Layers className="h-5 w-5 text-red-600" /> Apps de plataforma
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Gestiona los íconos del selector de entorno en el home de OkGo (estilo Grab).
                        Cada app puede tener su propia imagen, colores y experiencia de home.
                    </p>
                </div>
                <Button onClick={() => setEditing({ ...BLANK })} className="bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4 mr-1" /> Nueva app
                </Button>
            </div>

            {/* Info notice */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800">
                <p className="font-semibold mb-1">💡 Cómo funciona el selector de apps</p>
                <p>Los íconos se muestran como un carrusel prominente en la parte superior del home. Cuando el usuario selecciona una app, el home cambia completamente: se filtran los negocios por la super-categoría vinculada y el tema de color cambia al de la app seleccionada.</p>
            </div>

            {/* Apps list */}
            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
            ) : apps.length === 0 ? (
                <div className="rounded-xl border border-dashed bg-card p-10 text-center">
                    <Layers className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">Sin apps de plataforma aún.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {apps.map(app => (
                        <div key={app.id || app.slug} className="flex items-center gap-3 rounded-xl border bg-card p-3">
                            {/* Drag handle placeholder */}
                            <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-grab" />

                            {/* Icon preview */}
                            <div
                                className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                                style={{ background: `linear-gradient(135deg, ${app.gradient_from || app.color}, ${app.gradient_to || app.color})` }}
                            >
                                {app.icon_image_url
                                    ? <img src={app.icon_image_url} alt="" className="w-7 h-7 object-contain" />
                                    : <span className="text-2xl">{app.icon || '🛍️'}</span>
                                }
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm">{app.name}</span>
                                    <span className="font-mono text-[10px] text-muted-foreground">{app.slug}</span>
                                    <Badge variant={app.active ? 'default' : 'secondary'}
                                        className={`text-[10px] py-0 ${app.active ? 'bg-green-100 text-green-700 border-green-300' : ''}`}>
                                        {app.active ? 'Activa' : 'Inactiva'}
                                    </Badge>
                                    {app.external && <Badge variant="outline" className="text-[10px] py-0">Externo</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">{app.tagline}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-muted-foreground font-mono">{app.href}</span>
                                    {app.super_category_slug && (
                                        <Badge variant="outline" className="text-[10px] py-0">cat: {app.super_category_slug}</Badge>
                                    )}
                                </div>
                            </div>

                            {/* Order */}
                            <span className="text-xs text-muted-foreground flex-shrink-0">#{app.order}</span>

                            {/* Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => handleToggle(app)}
                                    className={`p-1.5 rounded hover:bg-secondary ${app.active ? 'text-green-600' : 'text-muted-foreground'}`}
                                    title={app.active ? 'Desactivar' : 'Activar'}>
                                    {app.active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                </button>
                                {app.href && !app.external && (
                                    <a href={app.href} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-secondary text-muted-foreground">
                                        <ExternalLink className="h-4 w-4" />
                                    </a>
                                )}
                                <button onClick={() => setEditing({ ...app })} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => setDeleteId(app.slug)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Editor dialog */}
            <AppEditor open={!!editing} value={editing} onChange={setEditing} onSave={handleSave} loading={saving} />

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Eliminar app</DialogTitle>
                        <DialogDescription>Esta acción no se puede deshacer. La app dejará de aparecer en el home.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
